import {MapState} from 'src/common/map/MapState';
import PixiCanvas from 'src/Pixi';
import {wallRenderer} from 'src/Pixi/map/WallRenderer';
import State from './states/State';
import {PlaceCannon} from './actions/PlaceCannon';
import {BuildWall} from './actions/BuildWall';
import {FireProjectile} from './actions/FireProjectile';
import {DamageWall} from './actions/DamageWall';
import {SelectSpawn} from './actions/SelectSpawn';
import {heldRenderer} from 'src/Pixi/map/HeldRenderer';
import HudRenderer from 'src/Pixi/HudRenderer';
import {SetWallPiece} from './actions/SetWallPiece';
import {Location, Loc} from '../common/map/Location';
import {Action, SetupGameAction, SetupPlayerState} from 'src/Action';
import {PointerMove} from './actions/PointerMove';
import Connection from 'src/Connection';
import {SmoothPointer} from './SmoothPointer';
import {pointerRenderer} from 'src/Pixi/PointerRenderer';
import {PlayerLeft} from './actions/PlayerLeft';
import {BuildabilityService} from 'src/common/services/BuildabilityService';
import {BuildabilityComponent} from 'src/common/components/BuildabilityComponent';
import {GameMode, gameModes} from 'src/common/modes/GameMode';
import {RemoveWalls} from "./actions/RemoveWalls";

export type GameStateName = 'select' | 'place' | 'fire' | 'build' | 'end';

export interface PlayerState {
    index: number;
    name: string;
    cannonsPlacedBefore: number;
    cannonsLeftToPlace: number;
    hasSelectedSpawn: boolean;
    wallPiece: Loc[];
    isAlive: boolean;
}

export class Game {
    public stateName: GameStateName;
    public currentState: State;
    public hasLeft: Set<number> = new Set<number>();
    public players: PlayerState[];
    public map: MapState;
    public clientDamaged: Set<string> = new Set<string>();
    public serverDamaged: Set<string> = new Set<string>();
    public index: number;
    private tickInterval?: number;
    private smoothPointer: SmoothPointer = new SmoothPointer();
    private pendingActions: Action[] = [];
    public mode: GameMode;
    public buildability: BuildabilityComponent = new BuildabilityComponent();
    private buildabilityService = new BuildabilityService(this);

    public constructor(public controller: PixiCanvas) {
        // Sets instance variables
    }

    public runServerActions(actions: Action[]): void {
        this.pendingActions.push(...actions);
        while (this.pendingActions.length > 0) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const action = this.pendingActions.shift()!;
            if (!this.runServerAction(action)) break;
        }
    }

    private runServerAction(action: Action): boolean {
        switch (action.kind) {
            case 'setup':
                this.setup(action);
                return false;

            case 'set-game-state':
                this.setGameState(action.state);
                break;

            case 'select-spawn':
                new SelectSpawn(this).byServer(action);
                break;

            case 'place-cannon':
                new PlaceCannon(this).byServer(action);
                break;

            case 'set-wall-piece':
                new SetWallPiece(this).byServer(action);
                break;

            case 'build-wall':
                new BuildWall(this).byServer(action);
                break;

            case 'damage-wall':
                new DamageWall(this).byServer(action);
                break;

            case 'remove-walls':
                new RemoveWalls(this).byServer(action);
                break;

            case 'fire-projectile':
                new FireProjectile(this).byServer(action);
                break;

            case 'player-left':
                new PlayerLeft(this).byServer(action);
                break;

            case 'pointer-move':
                new PointerMove(this).byServer(action);
                break;

            default:
                // eslint-disable-next-line no-console
                console.log('unexpected action', action);
                break;
        }
        return true;
    }

    private setup(action: SetupGameAction): void {
        this.index = action.index;
        this.players = action.players.map(player => this.setupPlayerState(player));
        this.map = MapState.fromSetupState(action.map);
        const mode = new gameModes[this.map.modeName]();
        this.setGameMode(mode);
        this.controller.finishSetup();
    }

    private setGameMode(mode: GameMode): void {
        this.mode = mode;
        this.mode.game = this;
    }

    private setupPlayerState(player: SetupPlayerState): PlayerState {
        return {
            index: player.index,
            name: player.name,
            cannonsPlacedBefore: 0,
            cannonsLeftToPlace: 0,
            hasSelectedSpawn: false,
            wallPiece: [],
            isAlive: true,
        };
    }

    private setGameState(name: GameStateName): void {
        if (this.stateName === name) return;
        const StateClass = this.mode.states[name];
        const state = new StateClass();

        if (this.currentState && this.currentState.finish) {
            this.currentState.finish();
        }

        wallRenderer.setMap(this.map);
        heldRenderer.setMap(this.map);

        this.stateName = name;
        this.currentState = state;
        this.currentState.controller = this.controller;
        this.currentState.start();

        this.controller.onChangeGameState();
    }

    public clearDamagedWalls(): void {
        this.map.clearDamagedWalls();
        heldRenderer.redraw();
        wallRenderer.redraw();
    }

    public updateAlivePlayers(): void {
        const heldCounts = this.map.countHeldSpawns();
        this.players.forEach(player => {
            const count = heldCounts.get(player.index) || 0;
            player.isAlive = !this.hasLeft.has(player.index) && count > 0;
            if (!player.isAlive) {
                HudRenderer.killPlayer(player);
                pointerRenderer.hidePointer(player.index);
            }
        });
    }

    public updateCannonPlaceCounts(): void {
        const spawnsHeld = this.map.countHeldSpawns();
        this.players.forEach(player => {
            const spawnCount = spawnsHeld.get(player.index) || 0;
            const cannonCount = spawnCount + 1;
            player.cannonsPlacedBefore = this.map.cannons.filter(
                cannon => cannon.owner === player.index
            ).length;
            player.cannonsLeftToPlace = player.isAlive ? cannonCount : 0;
        });
    }

    public resolveDamaged(): void {
        this.clientDamaged.forEach(key => {
            if (!this.serverDamaged.has(key)) {
                const loc = Location.fromString(key);
                const wall = this.map.walls[loc.y][loc.x];
                if (wall) {
                    wall.damaged = false;
                }
            }
        });
        this.clientDamaged.clear();
        this.serverDamaged.clear();
        wallRenderer.redraw();
    }

    public get currentPlayer(): PlayerState {
        return this.players[this.index];
    }

    public start(): void {
        this.tickInterval = window.setInterval(() => this.tick(), 100);
    }

    public end(): void {
        if (this.tickInterval) {
            window.clearTimeout(this.tickInterval);
            this.tickInterval = undefined;
        }
    }

    public get isPlaying(): boolean {
        return Boolean(this.tickInterval);
    }

    private tick(): void {
        this.sendPointerLocation();
        this.buildabilityService.tick();
        this.buildabilityService.lateTick();
    }

    private sendPointerLocation(): void {
        const tile = this.currentState.getCursorTile();
        this.smoothPointer.setLocation(tile);
        const vel = this.smoothPointer.getVelocity();
        const loc = tile.sub(new Location(0.5, 0.5));
        Connection.send('movePointer', {x: loc.x, y: loc.y, velX: vel.x, velY: vel.y});
    }
}



// WEBPACK FOOTER //
// ./src/game/Game.ts